import { jsxs, jsx } from 'react/jsx-runtime';
import { useState, useEffect, cloneElement } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import clsx from 'clsx';
import { Placements, TooltipTriggers } from '../types/index.js';
import { ArrowIcon } from '../ArrowIcon/index.js';
import { useStyles } from '../styles/index.js';
import { useClickOutsideRef } from '../../../hooks/src/useClickOutsideRef/index.js';

const getTooltipContent = (content, container) => createPortal(content, container);
function Tooltip({ target, hasTestIds, placement = Placements.Top, trigger = TooltipTriggers.Hover, children, dataTestId, container = document.body, zIndex = 1, className, rootClassName, preventOutsideClick, fallbackPlacements = [Placements.Top, Placements.Bottom], isOpen, notClickable, hideArrow = false, }) {
    const ownStyles = useStyles({ zIndex, notClickable });
    const [targetRef, setTargetElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const [arrowElement, setArrowElement] = useState(null);
    const [show, setShow] = useState(false);
    const toggle = () => {
        setShow(prev => !prev);
    };
    const isControlled = isOpen !== undefined;
    useEffect(() => {
        if (isControlled) {
            setShow(isOpen);
        }
    }, [isOpen, isControlled]);
    const { styles, attributes, state, } = usePopper(targetRef, popperElement, {
        modifiers: [
            {
                name: 'offset',
                enabled: true,
                options: {
                    offset: [0, 12], // todo check
                },
            },
            {
                name: 'arrow',
                enabled: true,
                options: {
                    element: arrowElement,
                },
            },
            {
                name: 'flip',
                enabled: true,
                options: {
                    fallbackPlacements,
                },
            },
        ],
        placement,
    });
    const popperContentRef = useClickOutsideRef(() => !preventOutsideClick || !isOpen && setShow(false));
    const arrowLocation = state?.placement || placement;
    const getContent = () => (jsxs("div", { ref: ref => setPopperElement(ref), style: { ...styles.popper }, ...attributes.popper, className: clsx(ownStyles.root, className), "data-testid": "tooltip-content", "data-popper-content": true, children: [!hideArrow && (jsx("div", { className: ownStyles.indicatorWrapper, style: styles.arrow, ref: ref => setArrowElement(ref), "data-arrow-wrapper-dir": arrowLocation, "data-popper-arrow": true, children: jsx(ArrowIcon, { className: ownStyles.indicator, "data-arrow-dir": arrowLocation }) })), jsx("div", { className: ownStyles.content, children: children })] }));
    const open = () => {
        if (!isControlled) {
            setShow(true);
        }
    };
    const close = () => {
        if (!isControlled) {
            setShow(false);
        }
    };
    return (jsxs("div", { ref: popperContentRef, "data-testid": dataTestId || 'tooltip-root', className: rootClassName, children: [cloneElement(target, {
                ref: (ref) => setTargetElement(ref),
                'data-popper-target': true,
                ...(!isControlled && { onMouseEnter: trigger === TooltipTriggers.Hover ? open : null }),
                ...(!isControlled && {
                    onClick: (e) => {
                        target.props.onClick?.(e);
                        trigger === TooltipTriggers.Click && toggle();
                    },
                }),
                ...(!isControlled && { onMouseLeave: trigger === TooltipTriggers.Hover ? close : null }),
                ...(!hasTestIds && { 'data-testid': 'tooltip-target' }),
            }), show && (getTooltipContent(getContent(), container))] }));
}
Tooltip.displayName = 'Tooltip';

export { Tooltip };
